import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';

/**
 * Dropdown menu anchored to the user name in the topbar. Same grouped
 * structure as the /account sidebar, so users can jump straight to any
 * settings page without first landing on /account.
 *
 * Open: click the trigger. Close: click outside, press Escape, or pick
 * a destination. The trigger button itself stays styled like a link
 * (no chrome) so the topbar feels light.
 */
export default function UserMenu() {
  const { user, organization, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const role = organization?.role;
  const isAdmin = role === 'owner' || role === 'admin';

  // Trial countdown from auth/me (no extra network call)
  const planTier = organization?.plan_tier;
  const trialEndsAt = organization?.trial_ends_at;
  const isTrial = planTier === 'trial' || planTier == null;
  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt) - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  // Click-outside + Escape to close
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const displayName = user.display_name || user.email;
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className="user-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`user-link ${open ? 'open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="user-link-avatar" aria-hidden="true">{initial}</span>
        <span className="user-link-text">
          <span className="user-link-sublabel">
            {isAdmin ? '🛡 My account' : 'My account'}
          </span>
          <span className="user-link-name">{displayName}</span>
        </span>
        <span className="user-link-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-header">
            <div className="user-menu-name">{user.display_name || user.email}</div>
            <div className="user-menu-email muted small">{user.email}</div>
            {organization && (
              <div className="muted small">
                {organization.name} · <span className="user-menu-role">{role}</span>
              </div>
            )}
            {isTrial && trialDaysLeft !== null && (
              <Link
                to="/account/billing"
                className={`user-menu-trial-badge ${trialDaysLeft <= 3 ? 'urgent' : ''}`}
                onClick={() => setOpen(false)}
              >
                {trialDaysLeft > 0
                  ? `⏳ Trial — ${trialDaysLeft}d left · Upgrade`
                  : '⚠️ Trial ended · Upgrade now'}
              </Link>
            )}
          </div>

          <MenuGroup title="Account">
            <MenuItem to="/account"            icon="👤" label="Profile"      onPick={() => setOpen(false)} />
            <MenuItem to="/account/password"   icon="🔑" label="Password"     onPick={() => setOpen(false)} />
            <MenuItem to="/account/appearance" icon="🎨" label="Appearance"   onPick={() => setOpen(false)} />
          </MenuGroup>

          <MenuGroup title="Organization">
            <MenuItem to="/account/organization" icon="🏢" label="Organization" onPick={() => setOpen(false)} />
            <MenuItem to="/account/team"         icon="👥" label="Team"         onPick={() => setOpen(false)} />
            <MenuItem to="/account/support"      icon="🛟" label="Support"      onPick={() => setOpen(false)} />
            {isAdmin && (
              <MenuItem to="/account/billing" icon="💳" label="Billing & plan" onPick={() => setOpen(false)} />
            )}
          </MenuGroup>

          {isAdmin && (
            <MenuGroup title="Admin">
              <MenuItem to="/account/roadmap" icon="🗺️" label="Roadmap"   onPick={() => setOpen(false)} />
              <MenuItem to="/account/audit"   icon="📋" label="Audit log" onPick={() => setOpen(false)} />
            </MenuGroup>
          )}

          {/* Platform operator only — users.is_platform_admin, NOT the org
              role. Server enforces via requirePlatformAdmin (non-operators
              get 403 regardless of what renders here). */}
          {user.is_platform_admin && (
            <MenuGroup title="Platform">
              <MenuItem to="/admin"        icon="🛠️" label="Admin dashboard" onPick={() => setOpen(false)} />
              <MenuItem to="/admin/quotas" icon="📊" label="Quotas"          onPick={() => setOpen(false)} />
            </MenuGroup>
          )}

          <div className="user-menu-footer">
            <button
              type="button"
              className="user-menu-signout"
              onClick={() => { setOpen(false); logout(); }}
            >
              ⏻ Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuGroup({ title, children }) {
  return (
    <div className="user-menu-group">
      <h4 className="user-menu-group-title">{title}</h4>
      {children}
    </div>
  );
}

function MenuItem({ to, icon, label, onPick }) {
  return (
    <Link to={to} className="user-menu-item" onClick={onPick} role="menuitem">
      <span className="user-menu-item-icon" aria-hidden="true">{icon}</span>
      <span>{label}</span>
    </Link>
  );
}
